import React from 'react';
import { Building2Icon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import type { Division, MetricType, PeriodInfo } from '../../types';
import { ProgressBar } from '../ui/ProgressBar';
import { Badge } from '../ui/Badge';
import { TeamSection } from './TeamSection';
import { aggregateDivisionMetrics, getProgressPercentage, getProgressStatus, getProgressBorderColor, calculateProjection } from '../../utils/calculations';
interface DivisionSectionProps {
  division: Division;
  period: PeriodInfo;
  metric: MetricType;
  expanded: boolean;
  onToggle: () => void;
}
export function DivisionSection({
  division,
  period,
  metric,
  expanded,
  onToggle
}: DivisionSectionProps) {
  const {
    current,
    goal
  } = aggregateDivisionMetrics(division, metric);
  const percentage = getProgressPercentage(current, goal);
  const projected = calculateProjection(current, period.daysElapsed, period.totalDays);
  const projPct = getProgressPercentage(projected, goal);
  const status = getProgressStatus(projPct);
  const borderColor = getProgressBorderColor(percentage);
  return <section className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors`} data-id="element-25">
      {/* Division Header */}
      <button onClick={onToggle} className={`w-full flex items-center gap-4 p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors border-l-4 ${borderColor}`} aria-expanded={expanded} aria-label={`${division.name} — ${expanded ? 'collapse' : 'expand'}`} data-id="element-26">
        {/* Chevron */}
        <div className="flex-shrink-0 text-gray-400 dark:text-gray-500" data-id="element-27">
          {expanded ? <ChevronDownIcon className="w-5 h-5" data-id="element-28" /> : <ChevronRightIcon className="w-5 h-5" data-id="element-29" />}
        </div>

        {/* Division Info */}
        <div className="flex items-center gap-3 w-52 flex-shrink-0" data-id="element-30">
          <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0" data-id="element-31">
            <Building2Icon className="w-5 h-5 text-slate-600 dark:text-slate-300" data-id="element-32" />
          </div>
          <div className="text-left min-w-0" data-id="element-33">
            <p className="text-sm font-bold text-gray-900 dark:text-white truncate" data-id="element-34">
              {division.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-35">
              Lead: {division.leadName}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="flex-1 min-w-0" data-id="element-36">
          <ProgressBar current={current} goal={goal} size="md" showLabel={true} data-id="element-37" />
        </div>

        {/* Status Badge */}
        <div className="flex-shrink-0" data-id="element-38">
          <Badge status={status} size="md" data-id="element-39" />
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && <div className="px-6 pb-6 pt-2" data-id="element-40">
          {division.teams.map(team => <TeamSection key={team.id} team={team} period={period} metric={metric} data-id="element-41" />)}
        </div>}
    </section>;
}